import { Link } from "react-router-dom";
import { ArrowUpRight, MessageSquare, Briefcase, Coins, Workflow, Bell, BarChart3, Users } from "lucide-react";
import SEO from "@/components/SEO";
import CtaBanner from "@/components/CtaBanner";

const PRODUCTS = [
  {
    name: "WhatsApp CRM",
    color: "#25D366",
    icon: MessageSquare,
    desc: "Convert WhatsApp chats into a structured sales pipeline. Auto-replies, broadcast lists, lead labels and team inboxes — all in one place.",
    features: ["Multi-agent inbox", "Broadcast & template messages", "Lead pipeline & tags", "Auto-replies + chatbots", "WhatsApp Business API"],
  },
  {
    name: "Worknex",
    color: "#2D2D2D",
    icon: Workflow,
    desc: "Workflow & operations tool for service businesses. Tasks, team check-ins, attendance, and field-team management in one app.",
    features: ["Field team tracking", "Daily check-in / attendance", "Task assignment", "Performance dashboards", "Mobile-first"],
  },
  {
    name: "Earn Yatra",
    color: "#FF9900",
    icon: Coins,
    desc: "Affiliate & rewards platform built for D2C brands. Reward your customers and partners with cashback, referrals, and loyalty points.",
    features: ["Referral programs", "Cashback campaigns", "Loyalty points", "Partner / affiliate tracking", "Wallet & redemption"],
  },
];

const FEATURES = [
  { icon: Users, t: "Lead Tracking", d: "Capture leads from website, WhatsApp, ads — into one CRM." },
  { icon: Bell, t: "Automation", d: "Trigger journeys for new leads, abandoned carts, repeat orders." },
  { icon: BarChart3, t: "Customer Engagement", d: "Broadcasts, drip campaigns, win-back flows." },
  { icon: Briefcase, t: "Workflow Management", d: "Assign tasks, track ops, measure team output." },
];

export default function CrmSolutions() {
  return (
    <>
      <SEO
        title="CRM Solutions — WhatsApp CRM, Worknex & Earn Yatra"
        description="Orina CRM Solutions: WhatsApp CRM, Worknex workflow tool, and Earn Yatra rewards platform. Lead tracking, automation, customer engagement and team management."
        path="/crm-solutions"
      />

      <section className="border-b-2 border-black bg-white py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <div className="tape">CRM Solutions</div>
          <h1 className="display mt-6 max-w-4xl text-5xl font-black leading-[0.95] tracking-tight md:text-6xl lg:text-7xl">
            CRM, workflow & rewards — <span className="bg-[#FFD700] px-2">all under one roof.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-base text-black/70 md:text-lg">
            Three products designed for Indian D2C brands and service businesses. Track every lead, automate every follow-up, and reward every customer.
          </p>
        </div>
      </section>

      <section className="bg-[#FAFAF7] py-20 md:py-24">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <div className="space-y-8">
            {PRODUCTS.map((p, i) => {
              const Icon = p.icon;
              return (
                <div
                  key={p.name}
                  data-testid={`crm-product-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
                  className="card-brutal grid grid-cols-1 items-stretch overflow-hidden p-0 md:grid-cols-12"
                >
                  <div
                    className="flex flex-col justify-center p-8 md:col-span-5 md:p-12"
                    style={{ background: p.color, color: "#fff" }}
                  >
                    <div className="flex h-14 w-14 items-center justify-center border-2 border-black bg-white text-black">
                      <Icon size={26} />
                    </div>
                    <div className="display mt-6 text-3xl font-black tracking-tight md:text-4xl lg:text-5xl">{p.name}</div>
                    <div className="mt-2 text-xs font-bold uppercase tracking-widest text-white/80">Product 0{i + 1}</div>
                  </div>
                  <div className="flex flex-col p-8 md:col-span-7 md:p-12">
                    <p className="text-base leading-relaxed text-black/85 md:text-lg">{p.desc}</p>
                    <ul className="mt-5 grid grid-cols-1 gap-2 sm:grid-cols-2">
                      {p.features.map((f) => (
                        <li key={f} className="flex items-start gap-2 text-sm">
                          <span className="mt-1 h-1.5 w-1.5 shrink-0 bg-black" /> {f}
                        </li>
                      ))}
                    </ul>
                    <div className="mt-6">
                      <Link to={`/contact/?type=crm&product=${encodeURIComponent(p.name)}`} className="btn-brutal" data-testid={`crm-cta-${i}`}>
                        Request Demo <ArrowUpRight size={16} />
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-white py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <div className="eyebrow">Common Capabilities</div>
          <h2 className="display mt-3 text-3xl font-black tracking-tight md:text-4xl">What you can do across all three.</h2>
          <div className="mt-10 grid grid-cols-1 gap-0 md:grid-cols-2 lg:grid-cols-4">
            {FEATURES.map((f) => (
              <div key={f.t} className="-ml-[2px] -mt-[2px] border-2 border-black bg-white p-6 hover:bg-[#FFD700]">
                <div className="flex h-10 w-10 items-center justify-center border-2 border-black bg-[#FFD700]">
                  <f.icon size={18} />
                </div>
                <div className="display mt-4 text-lg font-black">{f.t}</div>
                <p className="mt-2 text-sm text-black/70">{f.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CtaBanner
        title="Want a CRM tailored to your business?"
        subtitle="Tell us your workflow — we'll show you the right product (or build a custom integration)."
        primaryLabel="Get a Demo"
        primaryTo="/contact/?type=crm"
      />
    </>
  );
}
